import React, { useEffect, useMemo, useRef, useState } from "react";

import { useSelector } from "react-redux";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import { SellersBoard } from "../../../../services/v2/sales.service";
import { apiError, monthOf, saveBlob, usePerms } from "../kits/shared";
import { Ranking } from "../sales/metasBonos";
import { Chip, Sub, Ventana, monto } from "./piezasTienda";

/*
	Ventas › Vendedores (Módulos Claros, 2026-11-01): asesoras y maestras juntas (eran Maestros › Tablero y Metas › Ranking).
	Ventas, kits y cobrado del mes con la misma atribución de los bonos; % con kit de los grupos de cada maestra; la columna Bono
	y las metas solo salen cuando existen. «País» (ranking entre planteles, sección 67) y «Excel» (horarios de las maestras, sección 68)
	son los dos accesos que tenían Metas y Bonos y Maestros, con el mismo permiso.
*/

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const nombreMes = (m: string) => { const [y, n] = m.split("-").map(Number); return MESES[n - 1] + " " + y; };
const mover = (m: string, d: number) => { const [y, n] = m.split("-").map(Number); const f = new Date(y, n - 1 + d, 1); return monthOf(f); };

export interface TabVendedoresProps { campusId: number; sym: string; movil: boolean; q: string; section: "kitSales" | "teacherBoard" }

const TabVendedores: React.FC<TabVendedoresProps> = ({ campusId, sym, movil, q, section }) => {
	const [mes, setMes] = useState<string>(monthOf());
	const [tipo, setTipo] = useState<"" | "Asesora" | "Maestra">("");
	const [data, setData] = useState<SellersBoard | null>(null);
	const [error, setError] = useState<string | null>(null);
	const requestRef = useRef<number>(0);
	const currentUser: any = useSelector((state: RootState) => state.userProfile.value);
	const superAdmin = currentUser?.currentRole === "superAdmin";
	const p67: any = usePerms("salesGoals");
	const p68: any = usePerms("teacherBoard");
	const verPais = superAdmin || !!p67?.read;
	const verExcel = superAdmin || !!p68?.read;
	const [pais, setPais] = useState<boolean>(false);
	const [excelBusy, setExcelBusy] = useState<boolean>(false);
	const bajarExcel = () => {
		setExcelBusy(true);
		setError(null);
		HttpManager.getInstance().salesService.teacherExcel(campusId, mes)
			.then((r) => saveBlob(r.data, "Maestros_" + mes + ".xlsx", "Ventas de tienda"))
			.catch((e) => setError(apiError(e, "No se pudo generar el Excel.")))
			.finally(() => setExcelBusy(false));
	};
	useEffect(() => {
		const request = ++requestRef.current;
		setData(null);
		setError(null);
		HttpManager.getInstance().salesService.sellers(campusId, mes, section)
			.then((r) => { if (request === requestRef.current) setData(r.data); })
			.catch((e) => { if (request === requestRef.current) setError(apiError(e, "No se pudo cargar.")); });
	}, [campusId, mes, section]);

	const sinAcentos = (s: string) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
	const filas = useMemo(() => (data?.people || []).filter((p) => (!tipo || p.kind === tipo) && (!q || sinAcentos(p.name).includes(sinAcentos(q)))), [data, tipo, q]);
	const pct = (v: number | null | undefined) => v === null || v === undefined ? "-" : v.toLocaleString("es-MX", { maximumFractionDigits: 1 }) + " %";
	const conBono = !!data?.hasRules;
	const meta = (real: string, goal: string | null, avance: number | null | undefined) => goal === null ? null : <Sub>Meta {goal}{avance !== null && avance !== undefined ? ", " + pct(avance) : ""}</Sub>;

	const lista = () => {
		if (!data) return error ? null : <div className="loader" />;
		if (filas.length < 1) return <div className="wvtSub" style={{ textAlign: "center", padding: 24, fontSize: 15 }}>Sin ventas en el mes.</div>;
		if (movil) return <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingBottom: 8 }}>
			{filas.map((p) => <div key={p.key} className="wvtTarjeta" style={{ cursor: "default" }}>
				<div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><b>{p.name}</b><Chip tono="tenue">{p.kind}</Chip></div>
				<div style={{ fontSize: 14, marginTop: 4 }}>{p.sales} ventas · {p.kits} kits · <b>{monto(sym, p.amount)}</b></div>
				{p.kitRate !== null ? <Sub>{pct(p.kitRate)} con kit en {p.schedules} {p.schedules === 1 ? "grupo" : "grupos"}</Sub> : null}
				{conBono ? <Sub>Bono {monto(sym, p.bonus)}</Sub> : null}
			</div>)}
		</div>;
		return <table className="table wvtLista">
			<thead><tr><th>Persona</th><th style={{ width: 110 }}>Tipo</th><th className="wvtNum" style={{ width: 90 }}>Ventas</th><th className="wvtNum" style={{ width: 80 }}>Kits</th>
				<th className="wvtNum" style={{ width: 140 }}>Cobrado</th><th className="wvtNum" style={{ width: 150 }}>% con Kit de su Grupo</th>{conBono ? <th className="wvtNum" style={{ width: 120 }}>Bono</th> : null}</tr></thead>
			<tbody>{filas.map((p) => <tr key={p.key}>
				<td><b>{p.name}</b>{p.kind === "Asesora" && p.role ? <Sub>{p.role}</Sub> : null}</td>
				<td><Chip tono="tenue">{p.kind}</Chip></td>
				<td className="wvtNum">{p.sales}</td>
				<td className="wvtNum">{p.kits}</td>
				<td className="wvtNum">{monto(sym, p.amount)}</td>
				<td className="wvtNum">{p.kitRate === null ? <span className="wvtSub">-</span> : <>{pct(p.kitRate)}<Sub>{p.withKit} de {p.students}</Sub></>}</td>
				{conBono ? <td className="wvtNum">{monto(sym, p.bonus)}</td> : null}
			</tr>)}</tbody>
		</table>;
	};

	return <>
		<div className="waFmBarra">
			<div className="waFmBarraFecha">
				<div className="waFmSegs">
					<button type="button" className="waFmSeg" onClick={() => setMes(mover(mes, -1))} aria-label="Mes anterior">‹</button>
					<button type="button" className="waFmSeg activo" style={{ minWidth: 150 }}>{nombreMes(mes)}</button>
					<button type="button" className="waFmSeg" disabled={mes >= monthOf()} onClick={() => setMes(mover(mes, 1))} aria-label="Mes siguiente">›</button>
				</div>
			</div>
			<div className="waFmBarraListas">
				<div className="waFmSegs">
					{(["", "Asesora", "Maestra"] as const).map((t) => <button key={t || "todas"} type="button" className={"waFmSeg" + (tipo === t ? " activo" : "")} onClick={() => setTipo(t)}>{t ? t + "s" : "Todas"}</button>)}
				</div>
				{verPais ? <button type="button" className="btn btn-outline-secondary btn-sm" style={{ marginLeft: 8 }} onClick={() => setPais(true)}>País</button> : null}
				{verExcel ? <button type="button" className="btn btn-outline-secondary btn-sm" style={{ marginLeft: 8 }} disabled={excelBusy} onClick={bajarExcel}>Excel</button> : null}
			</div>
		</div>
		{data ? <div className="wvtKpis">
			<div><small>Kits Completos</small><b>{data.totals.kits}</b>{meta("", data.goal.kits !== null ? String(data.goal.kits) : null, data.totals.kitsPct)}</div>
			<div><small>Cobrado</small><b>{monto(sym, data.totals.amount)}</b>{meta("", data.goal.amount !== null ? monto(sym, data.goal.amount) : null, data.totals.amountPct)}</div>
			<div><small>Inscritas del Mes con Kit</small><b>{pct(data.totals.kitRate)}</b>{meta("", data.goal.kitRate !== null ? pct(data.goal.kitRate) : null, null)}</div>
		</div> : null}
		{error ? <div className="alert alert-danger" style={{ marginTop: 12 }}>{error}</div> : null}
		<div className="waScrollX" style={{ overflowX: "auto", marginTop: 14, paddingBottom: 8 }}>{lista()}</div>
		{pais ? <Ventana titulo="Ranking del País" onClose={() => setPais(false)}>
			<Ranking campusId={campusId} money={(v) => v === null || v === undefined ? "-" : monto(sym, v)} />
		</Ventana> : null}
	</>;
};

export default TabVendedores;
